// Analytics & Event Tracking System
import { supabase } from './supabase';

// Google Analytics 4 Integration
export const initGA4 = (measurementId: string) => {
  if (typeof window === 'undefined') return;

  // Load GA4 script
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: true,
    cookie_flags: 'SameSite=None;Secure',
  });
};

// Google Tag Manager Integration
export const initGTM = (gtmId: string) => {
  if (typeof window === 'undefined') return;

  // GTM script
  const script = document.createElement('script');
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `;
  document.head.appendChild(script);

  // GTM noscript
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.insertBefore(noscript, document.body.firstChild);
};

// Event Types
export enum AnalyticsEvent {
  // Page Views
  PAGE_VIEW = 'page_view',
  
  // Navigation
  NAV_CLICK = 'nav_click',
  FOOTER_LINK_CLICK = 'footer_link_click',
  CTA_CLICK = 'cta_click',
  
  // Directory
  LISTING_VIEW = 'listing_view',
  LISTING_CLICK = 'listing_click',
  QUOTE_REQUEST = 'quote_request',
  PHONE_CLICK = 'phone_click',
  EMAIL_CLICK = 'email_click',
  WEBSITE_CLICK = 'website_click',
  
  // Affiliate
  AFFILIATE_CLICK = 'affiliate_click',
  
  // Community
  FORUM_POST_VIEW = 'forum_post_view',
  FORUM_REPLY = 'forum_reply',
  EVENT_RSVP = 'event_rsvp',
  
  // Tools
  CALCULATOR_USE = 'calculator_use',
  CHECKLIST_COMPLETE = 'checklist_complete',
  QUIZ_COMPLETE = 'quiz_complete',
  
  // Citizenship
  CITIZENSHIP_CTA = 'citizenship_cta',
  BIRTH_CERT_CLICK = 'birth_cert_click',
  
  // AI Assistant
  AI_ASSISTANT_OPEN = 'ai_assistant_open',
  AI_ASSISTANT_MESSAGE = 'ai_assistant_message',
  
  // User Actions
  SIGN_UP = 'sign_up',
  SIGN_IN = 'sign_in',
  SIGN_OUT = 'sign_out',
  
  // Conversions
  NEWSLETTER_SUBSCRIBE = 'newsletter_subscribe',
  PREMIUM_UPGRADE = 'premium_upgrade',
  
  // Errors
  ERROR = 'error',
  API_ERROR = 'api_error',
}

interface EventParams {
  category?: string;
  label?: string;
  value?: number;
  listingId?: string;
  userId?: string;
  page?: string;
  url?: string;
  errorMessage?: string;
  errorStack?: string;
  [key: string]: any;
}

// Track event to both GA4 and Supabase
export const trackEvent = async (
  eventName: AnalyticsEvent | string,
  params: EventParams = {}
) => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    // Add common parameters
    const enrichedParams = {
      ...params,
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_url: window.location.href,
      user_id: user?.id || params.userId,
      session_id: getSessionId(),
    };

    // Track to Google Analytics
    if (window.gtag) {
      window.gtag('event', eventName, enrichedParams);
    }

    // Track to Supabase
    await supabase.from('analytics_events').insert({
      event_type: eventName,
      user_id: user?.id || params.userId,
      listing_id: params.listingId,
      metadata: enrichedParams,
    });

    // Log in development
    if (import.meta.env.DEV) {
      console.log('📊 Analytics Event:', eventName, enrichedParams);
    }
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

// Track page view
export const trackPageView = (path?: string) => {
  const pagePath = path || window.location.pathname;
  
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    });
  }

  trackEvent(AnalyticsEvent.PAGE_VIEW, {
    page: pagePath,
    title: document.title,
  });
};

// Track navigation clicks
export const trackNavClick = (linkName: string, destination: string) => {
  trackEvent(AnalyticsEvent.NAV_CLICK, {
    category: 'Navigation',
    label: linkName,
    url: destination,
  });
};

// Track CTA clicks
export const trackCTAClick = (ctaName: string, location: string) => {
  trackEvent(AnalyticsEvent.CTA_CLICK, {
    category: 'CTA',
    label: ctaName,
    location,
  });
};

// Track listing interactions
export const trackListingView = (listingId: string, listingName: string) => {
  trackEvent(AnalyticsEvent.LISTING_VIEW, {
    category: 'Directory',
    listingId,
    label: listingName,
  });
};

export const trackListingClick = (
  listingId: string,
  listingName: string,
  clickType: 'phone' | 'email' | 'website' | 'quote'
) => {
  const eventMap = {
    phone: AnalyticsEvent.PHONE_CLICK,
    email: AnalyticsEvent.EMAIL_CLICK,
    website: AnalyticsEvent.WEBSITE_CLICK,
    quote: AnalyticsEvent.QUOTE_REQUEST,
  };

  trackEvent(eventMap[clickType], {
    category: 'Directory',
    listingId,
    label: listingName,
  });
};

// Track affiliate clicks
export const trackAffiliateClick = async (
  listingId: string,
  listingName: string,
  affiliateUrl: string
) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    // Track to analytics
    trackEvent(AnalyticsEvent.AFFILIATE_CLICK, {
      category: 'Affiliate',
      listingId,
      label: listingName,
      url: affiliateUrl,
    });

    // Track to affiliate_clicks table for commission tracking
    await supabase.from('affiliate_clicks').insert({
      listing_id: listingId,
      user_id: user?.id,
      metadata: {
        listing_name: listingName,
        url: affiliateUrl,
        referrer: document.referrer,
      },
    });
  } catch (error) {
    console.error('Affiliate tracking error:', error);
  }
};

// Track AI Assistant usage
export const trackAIAssistant = (action: 'open' | 'message', details?: any) => {
  const eventName = action === 'open' 
    ? AnalyticsEvent.AI_ASSISTANT_OPEN 
    : AnalyticsEvent.AI_ASSISTANT_MESSAGE;

  trackEvent(eventName, {
    category: 'AI Assistant',
    label: action,
    ...details,
  });
};

// Track errors
export const trackError = (
  errorMessage: string,
  errorStack?: string,
  context?: any
) => {
  trackEvent(AnalyticsEvent.ERROR, {
    category: 'Error',
    errorMessage,
    errorStack,
    ...context,
  });

  // Also log to Supabase for error monitoring
  supabase.from('analytics_events').insert({
    event_type: 'error',
    metadata: {
      error_message: errorMessage,
      error_stack: errorStack,
      context,
      user_agent: navigator.userAgent,
      page: window.location.pathname,
      timestamp: new Date().toISOString(),
    },
  });
};

// Session management
const SESSION_KEY = 'analytics_session_id';
const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes

export const getSessionId = (): string => {
  const stored = localStorage.getItem(SESSION_KEY);
  const now = Date.now();

  if (stored) {
    try {
      const { id, timestamp } = JSON.parse(stored);
      if (now - timestamp < SESSION_DURATION) {
        // Update timestamp
        localStorage.setItem(SESSION_KEY, JSON.stringify({ id, timestamp: now }));
        return id;
      }
    } catch (e) {
      // Invalid stored data
    }
  }

  // Create new session
  const newId = `session_${now}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem(SESSION_KEY, JSON.stringify({ id: newId, timestamp: now }));
  return newId;
};

// Performance monitoring
export const trackPerformance = () => {
  if (typeof window === 'undefined' || !window.performance) return;

  window.addEventListener('load', () => {
    setTimeout(() => {
      const perfData = window.performance.timing;
      const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
      const connectTime = perfData.responseEnd - perfData.requestStart;
      const renderTime = perfData.domComplete - perfData.domLoading;

      trackEvent('performance', {
        category: 'Performance',
        page_load_time: pageLoadTime,
        connect_time: connectTime,
        render_time: renderTime,
      });
    }, 0);
  });
};

// Conversion tracking
export const trackConversion = (
  conversionType: string,
  value?: number,
  currency: string = 'EUR'
) => {
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'AW-CONVERSION_ID', // Replace with actual conversion ID
      value,
      currency,
      transaction_id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    });
  }

  trackEvent('conversion', {
    category: 'Conversion',
    label: conversionType,
    value,
    currency,
  });
};

// Declare global types
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export default {
  initGA4,
  initGTM,
  trackEvent,
  trackPageView,
  trackNavClick,
  trackCTAClick,
  trackListingView,
  trackListingClick,
  trackAffiliateClick,
  trackAIAssistant,
  trackError,
  trackPerformance,
  trackConversion,
  AnalyticsEvent,
};
